import { numberToEventSeverity } from "@sitescene/shared";
import { EventSeverity } from "@sitescene/types";
import {
	ArrowDown,
	ArrowRight,
	ArrowUp,
	ArrowUpRight,
} from "@untitledui/icons";

import {
	BadgeColors,
	IconComponentType,
} from "@/components/ui/badges/badge-types";

type SeverityConfig = {
	badgeColor: BadgeColors;
	badgeIcon: IconComponentType;
	badgeText: string;
	dotColorPing: string;
	dotColorSolid: string;
	highlightBgColorVar: string;
	highlightBorderColor: string;
	highlightDotColorPing: string;
	highlightDotColorSolid: string;
	highlightTextColorVar: string;
	selectLabel: string;
	toastClassName: string;
	videoBarClass: string;
	videoBarText: string;
};

export const severityConfig = (
	severity: EventSeverity | number,
): SeverityConfig => {
	const severityEnum =
		typeof severity === "number" ? numberToEventSeverity(severity) : severity;
	switch (severityEnum) {
		case EventSeverity.Critical:
			return {
				badgeColor: "pink",
				badgeIcon: ArrowUp as IconComponentType,
				badgeText: "Critical Priority",
				dotColorPing: "bg-utility-severity-critical-400",
				dotColorSolid: "bg-utility-severity-critical-700",
				highlightBgColorVar: "var(--color-utility-severity-critical-700)",
				highlightBorderColor: "var(--color-utility-severity-critical-200)",
				highlightDotColorPing: "var(--color-utility-severity-critical-400)",
				highlightDotColorSolid: "var(--color-utility-severity-critical-700)",
				highlightTextColorVar: "var(--color-white)",
				selectLabel: "Critical Priority",
				toastClassName:
					"bg-utility-severity-critical-700 ring-utility-severity-critical-200",
				videoBarClass:
					"bg-utility-severity-critical-50 text-utility-severity-critical-700 ring-utility-severity-critical-200",
				videoBarText: "Critical Priority",
			};
		case EventSeverity.High:
			return {
				badgeColor: "error",
				badgeIcon: ArrowUpRight as IconComponentType,
				badgeText: "High Priority",
				dotColorPing: "bg-utility-severity-high-400",
				dotColorSolid: "bg-utility-severity-high-500",
				highlightBgColorVar: "var(--color-utility-severity-high-500)",
				highlightBorderColor: "var(--color-utility-severity-high-200)",
				highlightDotColorPing: "var(--color-utility-severity-high-400)",
				highlightDotColorSolid: "var(--color-utility-severity-high-500)",
				highlightTextColorVar: "var(--color-white)",
				selectLabel: "High Priority and Above",
				toastClassName:
					"bg-utility-severity-high-500 ring-utility-severity-high-200",
				videoBarClass:
					"bg-utility-severity-high-50 text-utility-severity-high-700 ring-utility-severity-high-200",
				videoBarText: "High Priority",
			};
		case EventSeverity.Low:
			return {
				badgeColor: "success",
				badgeIcon: ArrowDown as IconComponentType,
				badgeText: "Low Priority",
				dotColorPing: "bg-utility-severity-low-400",
				dotColorSolid: "bg-utility-severity-low-500",
				highlightBgColorVar: "var(--color-utility-severity-low-500)",
				highlightBorderColor: "var(--color-utility-severity-low-200)",
				highlightDotColorPing: "var(--color-utility-severity-low-400)",
				highlightDotColorSolid: "var(--color-utility-severity-low-500)",
				highlightTextColorVar: "var(--color-white)",
				selectLabel: "Low Priority",
				toastClassName:
					"bg-utility-severity-low-500 ring-utility-severity-low-200",
				videoBarClass:
					"bg-utility-severity-low-50 text-utility-severity-low-700 ring-utility-severity-low-200",
				videoBarText: "Low Priority",
			};
		case EventSeverity.Moderate:
			return {
				badgeColor: "warning",
				badgeIcon: ArrowRight as IconComponentType,
				badgeText: "Moderate Priority",
				dotColorPing: "bg-utility-severity-moderate-400",
				dotColorSolid: "bg-utility-severity-moderate-500",
				highlightBgColorVar: "var(--color-utility-severity-moderate-500)",
				highlightBorderColor: "var(--color-utility-severity-moderate-200)",
				highlightDotColorPing: "var(--color-utility-severity-moderate-400)",
				highlightDotColorSolid: "var(--color-utility-severity-moderate-500)",
				highlightTextColorVar: "var(--color-white)",
				selectLabel: "Moderate Priority and Above",
				toastClassName:
					"bg-utility-severity-moderate-500 ring-utility-severity-moderate-200",
				videoBarClass:
					"bg-utility-severity-moderate-50 text-utility-severity-moderate-700 ring-utility-severity-moderate-200",
				videoBarText: "Moderate Priority",
			};
		default: {
			return {
				badgeColor: "gray",
				badgeIcon: ArrowDown as IconComponentType,
				badgeText: "Unknown Priority",
				dotColorPing: "bg-gray-400",
				dotColorSolid: "bg-gray-500",
				highlightBgColorVar: "var(--color-utility-neutral-500)",
				highlightBorderColor: "var(--color-utility-neutral-200)",
				highlightDotColorPing: "var(--color-gray-400)",
				highlightDotColorSolid: "var(--color-gray-500)",
				highlightTextColorVar: "var(--color-white)",
				selectLabel: "Any Priority",
				toastClassName: "bg-gray-500 ring-utility-neutral-200",
				videoBarClass:
					"bg-utility-neutral-50 text-utility-neutral-700 ring-utility-neutral-200",
				videoBarText: "Unknown Priority",
			};
		}
	}
};

export type SeverityHighlightCssVars = {
	"--highlight-bg-color": string;
	"--highlight-border-color": string;
	"--highlight-dot-color-ping": string;
	"--highlight-dot-color-solid": string;
	"--highlight-fg-color": string;
};

/** CSS custom properties for alert-list pulse and map cluster/pin colors. */
export const severityHighlightCssVars = (
	severity: EventSeverity | number,
): SeverityHighlightCssVars => {
	const config = severityConfig(severity);
	return {
		"--highlight-bg-color": config.highlightBgColorVar,
		"--highlight-border-color": config.highlightBorderColor,
		"--highlight-dot-color-ping": config.highlightDotColorPing,
		"--highlight-dot-color-solid": config.highlightDotColorSolid,
		"--highlight-fg-color": config.highlightTextColorVar,
	};
};
